import { useEffectWithController } from '@carvana/vdp-react-hooks';
import equals from 'ramda/es/equals';
import { useContext } from 'react';
import ErrorContext from '~/contexts/ErrorContext';
import InventoryContext from '~/contexts/InventoryContext';
import { useUser, useUserLocation } from '~/contexts/UserContext';
import type { Vehicle } from '~/contracts';
import useMemoCompare from '~/hooks/useMemoCompare';
import fetchAvailability from '~/services/fetchAvailability';
import generateErrorLogger, { logError } from '~/utilities/errorHandlers';
import { addAvailabilityToVehicles } from './helpers';

const useGetAvailability = () => {
  const {
    setSimilarVehicles,
    similarVehicles: { vehicles, searchRequestId }
  } = useContext(InventoryContext);
  const { updateResponseErrors } = useContext(ErrorContext);
  const { zip } = useUserLocation();
  const {
    data: { userId },
    isLoaded: userIsLoaded
  } = useUser();

  const stableVehicles = useMemoCompare<Vehicle[]>(vehicles, (prev, next) =>
    equals(
      prev.map(vehicle => vehicle.vehicleId),
      next.map(vehicle => vehicle.vehicleId)
    )
  );

  const shouldCallAvailability = stableVehicles.length && userIsLoaded;

  useEffectWithController(
    controller => {
      if (!shouldCallAvailability) return;

      fetchAvailability({
        zip,
        userId,
        vehicles: stableVehicles,
        searchRequestId,
        controller
      })
        .then(res => {
          if (res.data.status === 'Succeeded') {
            const updatedVehicles = addAvailabilityToVehicles(
              stableVehicles,
              res.data.content ?? []
            );

            setSimilarVehicles({ vehicles: updatedVehicles, searchRequestId });

            if (updatedVehicles.some(v => v.pickupInfo || v.deliveryInfo)) {
              updateResponseErrors({ lowAvailabilityError: null });
            }
          } else {
            logError(new Error(res.data.errorMessage), {
              location: 'Low Inventory Availability'
            });

            const vehicle = res.data.content?.find(
              v =>
                v.soonestDelivery?.errorMessage || v.soonestPickup?.errorMessage
            );

            const errorMessage =
              vehicle?.soonestDelivery?.errorMessage ||
              vehicle?.soonestPickup?.errorMessage;

            updateResponseErrors({ lowAvailabilityError: errorMessage });
          }
        })
        .catch(
          generateErrorLogger(
            'Availability',
            {},
            {
              callback: () => {
                updateResponseErrors({
                  lowAvailabilityError:
                    'Failed to fetch availability for low inventory.'
                });
              },
              controller
            }
          )
        );
    },
    [
      searchRequestId,
      shouldCallAvailability,
      setSimilarVehicles,
      stableVehicles,
      updateResponseErrors,
      userId,
      zip
    ]
  );
};

export default useGetAvailability;
